'use client';

import Image from 'next/image';

import { format } from 'date-fns';
import { Check, X, ArrowRight, Star, Award, TrendingUp } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { type AlternativesArticle } from '@/lib/alternatives-data';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

interface AlternativesPageContentProps {
  article: AlternativesArticle;
  lastUpdated: Date;
  locale?: string;
}

/** The chrome around the article body — everything not held in `AlternativesArticle`. */
type AlternativesUi = {
  breadcrumbHome: string;
  breadcrumbBlog: string;
  breadcrumbCurrent: (platform: string) => string;
  badge: string;
  byline: string;
  updated: (date: string) => string;
  readTime: (readTime: string) => string;
  topPickEyebrow: string;
  tryFree: (name: string) => string;
  bestOverall: string;
  alternativesHeading: (count: number, platform: string) => string;
  bestFor: string;
  pricingLabel: string;
  keyStrength: string;
  whatWeLike: string;
  watchOutFor: string;
  ourTake: string;
  bookDemo: string;
  tableIfYou: string;
  tableChoose: string;
  tableWhy: string;
  finalThoughtsHeading: string;
  ctaTitle: string;
  ctaBody: string;
  ctaPrimary: string;
  ctaSecondary: string;
  faqHeading: string;
  disclosureLabel: string;
  disclosureBody: string;
};

const UI: { en: AlternativesUi; ar: Partial<AlternativesUi> } = {
  en: {
    breadcrumbHome: 'Home',
    breadcrumbBlog: 'Blog',
    breadcrumbCurrent: (platform) => `${platform} Alternatives`,
    badge: 'Roundup',
    byline: 'By the Open Team',
    updated: (date) => `Updated ${date}`,
    readTime: (readTime) => `${readTime} read`,
    topPickEyebrow: 'Our #1 Pick',
    tryFree: (name) => `Try ${name} Free`,
    bestOverall: 'Best Overall',
    alternativesHeading: (count, platform) =>
      `The ${count} Best ${platform} Alternatives`,
    bestFor: 'Best For',
    pricingLabel: 'Pricing',
    keyStrength: 'Key Strength',
    whatWeLike: 'What we like',
    watchOutFor: 'Watch out for',
    ourTake: 'Our take:',
    bookDemo: 'Book a Demo',
    tableIfYou: 'If you...',
    tableChoose: 'Choose',
    tableWhy: 'Why',
    finalThoughtsHeading: 'Final Thoughts',
    ctaTitle: 'Ready to upgrade your support?',
    ctaBody:
      'Open achieves 77% automation rates with transparent pricing. Try it free alongside your current platform—no risk, no commitment.',
    ctaPrimary: 'Start Free Trial',
    ctaSecondary: 'Talk to Sales',
    faqHeading: 'Frequently Asked Questions',
    disclosureLabel: 'Disclosure:',
    disclosureBody:
      "We build Open, so yes, we're biased. But we try to be fair—if another platform genuinely fits your needs better, we'll say so. Our goal is helping you find the right tool, even if it's not ours.",
  },
  // TODO(i18n-ar): translate. An empty string or an omitted key falls back to
  // English, so this renders correctly at every stage of translation. The four
  // interpolating entries — `breadcrumbCurrent`, `updated`, `readTime`,
  // `tryFree`, `alternativesHeading` — are functions: add them as functions with
  // the same signature, keeping the interpolated value in place.
  ar: {
    breadcrumbHome: 'الرئيسية',
    breadcrumbBlog: 'المدونة',
    breadcrumbCurrent: (platform) => `بدائل ${platform}`,
    badge: 'مقارنة شاملة',
    byline: 'بقلم فريق Open',
    updated: (date) => `آخر تحديث ${date}`,
    readTime: (readTime) => `مدة القراءة ${readTime}`,
    topPickEyebrow: 'اختيارنا الأول',
    tryFree: (name) => `جرّب ${name} مجاناً`,
    bestOverall: 'الأفضل إجمالاً',
    alternativesHeading: (count, platform) =>
      `أفضل ${count} بدائل لـ ${platform}`,
    bestFor: 'الأنسب لـ',
    pricingLabel: 'التسعير',
    keyStrength: 'أبرز نقطة قوة',
    whatWeLike: 'اللي عجبنا',
    watchOutFor: 'انتبه لـ',
    ourTake: 'رأينا:',
    bookDemo: 'احجز عرضاً توضيحياً',
    tableIfYou: 'إذا كنت...',
    tableChoose: 'اختر',
    tableWhy: 'ليش',
    finalThoughtsHeading: 'خلاصة الكلام',
    ctaTitle: 'جاهز ترقّي دعم عملائك؟',
    ctaBody:
      'Open يحقق معدلات أتمتة 77% بتسعير شفاف. جرّبه مجاناً جنب منصتك الحالية — بلا مخاطرة وبلا التزام.',
    ctaPrimary: 'ابدأ التجربة المجانية',
    ctaSecondary: 'تواصل مع المبيعات',
    faqHeading: 'الأسئلة الشائعة',
    disclosureLabel: 'إفصاح:',
    disclosureBody:
      'إحنا اللي نبني Open، فأكيد عندنا انحياز. لكننا نحاول نكون منصفين — وإذا كانت منصة ثانية تناسب احتياجك فعلاً أكثر، بنقولها لك. هدفنا نساعدك تلاقي الأداة الصح، حتى لو ما كانت أداتنا.',
  },
};

/** English chrome, with any authored Arabic string spread over it. */
function resolveUi(locale?: string): AlternativesUi {
  if (locale !== 'ar') return UI.en;

  const resolved: Record<string, unknown> = { ...UI.en };
  for (const key of Object.keys(UI.ar) as (keyof AlternativesUi)[]) {
    const value = UI.ar[key];
    if (value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    resolved[key] = value;
  }
  return resolved as AlternativesUi;
}

export function AlternativesPageContent({
  article,
  lastUpdated,
  locale,
}: AlternativesPageContentProps) {
  const u = resolveUi(locale);
  const demoAriaLabel =
    locale === 'ar'
      ? 'اعرف أكثر عن Open واحجز عرضًا توضيحيًا'
      : 'Learn more about Open and book a demo';

  return (
    <article className="bg-white">
      {/* Header */}
      <header className="border-b border-neutral-100 pt-32 pb-12">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            {/* Breadcrumb */}
            <nav className="mb-6 text-sm text-neutral-500">
              <Link href="/" className="hover:text-neutral-900">
                {u.breadcrumbHome}
              </Link>
              <span className="mx-2">/</span>
              <Link href="/blog" className="hover:text-neutral-900">
                {u.breadcrumbBlog}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-neutral-900">
                {u.breadcrumbCurrent(article.platform)}
              </span>
            </nav>

            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-neutral-100 px-4 py-1.5 text-sm font-medium text-neutral-700">
              {u.badge}
            </div>

            {/* Title */}
            <h1 className="mb-6 font-serif text-4xl md:text-5xl">
              {article.heroTitle}
            </h1>

            {/* Subtitle */}
            <p className="mb-8 text-xl text-neutral-600 leading-relaxed italic">
              {article.heroSubtitle}
            </p>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 overflow-hidden rounded-full bg-neutral-100">
                  <Image
                    src="/images/about/mo.jpg"
                    alt="Author"
                    width={32}
                    height={32}
                    className="h-full w-full object-cover"
                  />
                </div>
                <span>{u.byline}</span>
              </div>
              <span className="text-neutral-300">|</span>
              <span>{u.updated(format(lastUpdated, 'MMMM d, yyyy'))}</span>
              <span className="text-neutral-300">|</span>
              <span>{u.readTime(article.readTime)}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <div className="py-12">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            {/* Intro */}
            <div className="prose prose-neutral prose-lg max-w-none prose-headings:font-serif prose-p:text-neutral-700 prose-p:leading-relaxed mb-16">
              {article.introHook.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>

            {/* Quick Jump to #1 Pick */}
            <div className="mb-16 rounded-xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Award className="h-5 w-5 text-green-600" />
                <span className="text-sm font-semibold uppercase tracking-wide text-green-700">
                  {u.topPickEyebrow}
                </span>
              </div>
              <h3 className="text-xl text-neutral-900 mb-2">
                {article.alternatives[0].name} —{' '}
                {article.alternatives[0].tagline}
              </h3>
              <p className="text-neutral-700 mb-4">
                {article.alternatives[0].keyStrength}
              </p>
              <Button asChild size="sm">
                <Link href="/demo" aria-label={demoAriaLabel}>
                  {u.tryFree(article.alternatives[0].name)}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            {/* Why People Leave */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl mb-8">
                {article.whyPeopleLeave.title}
              </h2>
              <div className="space-y-6">
                {article.whyPeopleLeave.reasons.map((reason, idx) => (
                  <div key={idx} className="border-l-4 border-red-200 pl-6">
                    <h3 className="text-neutral-900 mb-2">{reason.title}</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* What to Look For */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl mb-8">
                {article.whatToLookFor.title}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {article.whatToLookFor.criteria.map((criterion, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-neutral-200 bg-neutral-50 p-5"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="h-5 w-5 text-green-600" />
                      <h3 className="text-neutral-900">{criterion.title}</h3>
                    </div>
                    <p className="text-sm text-neutral-600 leading-relaxed">
                      {criterion.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* The Alternatives */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl mb-8">
                {u.alternativesHeading(
                  article.alternatives.length,
                  article.platform,
                )}
              </h2>

              <div className="space-y-12">
                {article.alternatives.map((alt) => (
                  <div
                    key={alt.rank}
                    className={cn(
                      'rounded-xl border-2 p-6 md:p-8',
                      alt.rank === 1
                        ? 'border-green-300 bg-gradient-to-br from-green-50/50 to-emerald-50/50'
                        : 'border-neutral-200 bg-white',
                    )}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span
                            className={cn(
                              'flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold',
                              alt.rank === 1
                                ? 'bg-green-500 text-white'
                                : 'bg-neutral-200 text-neutral-600',
                            )}
                          >
                            {alt.rank}
                          </span>
                          <h3 className="text-2xl text-neutral-900">
                            {alt.name}
                          </h3>
                          {alt.rank === 1 && (
                            <span className="rounded-full bg-green-500 px-3 py-0.5 text-xs font-medium text-white">
                              {u.bestOverall}
                            </span>
                          )}
                        </div>
                        <p className="text-neutral-500 italic">{alt.tagline}</p>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-neutral-700 leading-relaxed mb-6">
                      {alt.description}
                    </p>

                    {/* Key Info Grid */}
                    <div className="grid gap-4 sm:grid-cols-3 mb-6">
                      <div className="rounded-lg bg-white border border-neutral-200 p-3">
                        <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-1">
                          {u.bestFor}
                        </div>
                        <div className="text-sm font-medium text-neutral-900">
                          {alt.bestFor}
                        </div>
                      </div>
                      <div className="rounded-lg bg-white border border-neutral-200 p-3">
                        <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-1">
                          {u.pricingLabel}
                        </div>
                        <div className="text-sm font-medium text-neutral-900">
                          {alt.pricing}
                        </div>
                      </div>
                      <div className="rounded-lg bg-white border border-neutral-200 p-3">
                        <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-1">
                          {u.keyStrength}
                        </div>
                        <div className="text-sm font-medium text-neutral-900">
                          {alt.keyStrength}
                        </div>
                      </div>
                    </div>

                    {/* Pros and Cons */}
                    <div className="grid gap-6 sm:grid-cols-2 mb-6">
                      <div>
                        <h4 className="text-green-700 mb-3 flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          {u.whatWeLike}
                        </h4>
                        <ul className="space-y-2">
                          {alt.pros.map((pro, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-2 text-sm text-neutral-600"
                            >
                              <Check className="h-4 w-4 shrink-0 text-green-500 mt-0.5" />
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-red-700 mb-3 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {u.watchOutFor}
                        </h4>
                        <ul className="space-y-2">
                          {alt.cons.map((con, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-2 text-sm text-neutral-600"
                            >
                              <X className="h-4 w-4 shrink-0 text-red-400 mt-0.5" />
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Verdict */}
                    <div
                      className={cn(
                        'rounded-lg p-4',
                        alt.rank === 1 ? 'bg-green-100' : 'bg-neutral-100',
                      )}
                    >
                      <p
                        className={cn(
                          'text-sm font-medium leading-relaxed',
                          alt.rank === 1
                            ? 'text-green-900'
                            : 'text-neutral-700',
                        )}
                      >
                        <strong>{u.ourTake}</strong> {alt.verdict}
                      </p>
                    </div>

                    {/* CTA for #1 */}
                    {alt.rank === 1 && (
                      <div className="mt-6 flex flex-wrap gap-3">
                        <Button asChild>
                          <Link href="/demo" aria-label={demoAriaLabel}>
                            {u.tryFree(alt.name)}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                        <Button asChild variant="outline">
                          <Link href="/demo" aria-label={demoAriaLabel}>
                            {u.bookDemo}
                          </Link>
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* How to Choose */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl mb-8">
                {article.howToChoose.title}
              </h2>
              <div className="overflow-hidden rounded-xl border border-neutral-200">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {u.tableIfYou}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {u.tableChoose}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 hidden sm:table-cell">
                        {u.tableWhy}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {article.howToChoose.scenarios.map((scenario, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-neutral-100 last:border-0"
                      >
                        <td className="px-4 py-3 text-sm text-neutral-600">
                          {scenario.situation}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-neutral-900">
                          {scenario.recommendation}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-500 hidden sm:table-cell">
                          {scenario.why}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Migration Tips */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl mb-8">
                {article.migrationTips.title}
              </h2>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
                <ol className="space-y-4">
                  {article.migrationTips.tips.map((tip, idx) => (
                    <li key={idx} className="flex gap-4">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-200 text-xs font-bold text-amber-800">
                        {idx + 1}
                      </span>
                      <p className="text-amber-900 leading-relaxed">{tip}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </section>

            {/* Final Thoughts */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl mb-8">
                {u.finalThoughtsHeading}
              </h2>
              <div className="prose prose-neutral prose-lg max-w-none prose-p:text-neutral-700 prose-p:leading-relaxed">
                {article.finalThoughts.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </section>

            {/* CTA Box */}
            <div className="mb-16 rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-8 text-center">
              <h3 className="text-2xl text-blue-900 mb-3">{u.ctaTitle}</h3>
              <p className="text-blue-800 mb-6 max-w-xl mx-auto">{u.ctaBody}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button asChild size="lg">
                  <Link href="/demo" aria-label={demoAriaLabel}>
                    {u.ctaPrimary}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="bg-white"
                >
                  <Link href="/demo" aria-label={demoAriaLabel}>
                    {u.ctaSecondary}
                  </Link>
                </Button>
              </div>
            </div>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="font-serif text-3xl mb-8">{u.faqHeading}</h2>
              <Accordion type="single" collapsible className="w-full">
                {article.faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="border-b border-neutral-200"
                  >
                    <AccordionTrigger className="text-left font-medium hover:no-underline py-4 text-neutral-900">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-600 pb-4 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            {/* Footer Note */}
            <div className="border-t border-neutral-200 pt-8">
              <p className="text-sm text-neutral-500 leading-relaxed">
                <strong>{u.disclosureLabel}</strong> {u.disclosureBody}
              </p>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
